import { Avatar, Tooltip } from '@mantine/core'

import { EmployeeStatus } from 'src/graphql/types/employees'
import { buildFullName } from 'src/lib/formatters'

type EmployeeWithStatus = {
  employeeId?: string
  firstName?: string | null
  lastName?: string | null
  status?: string | null
  // For pre-formatted employee options (label/value format)
  label?: string
  value?: string
}

type EmployeeAvatarGroupProps = {
  leadEmployees: EmployeeWithStatus[]
  memberEmployees?: EmployeeWithStatus[]
  maxVisible?: number
}

const getEmployeeKey = (employee: EmployeeWithStatus): string => {
  return employee.employeeId || employee.value || ''
}

const getEmployeeName = (employee: EmployeeWithStatus): string => {
  if (employee.label) {
    // Remove any parenthetical suffixes like "(Admin)" or "(Inactive)"
    return employee.label.replace(/\s*\(.*?\)\s*$/, '').trim()
  }
  return buildFullName(employee)
}

const getEmployeeInitials = (employee: EmployeeWithStatus): string => {
  const name = getEmployeeName(employee)
  if (!name) return ''

  const parts = name.split(' ')
  const firstName = parts[0] || ''
  const lastName = parts[parts.length - 1] || ''
  return `${firstName.charAt(0)}${lastName.charAt(0)}`
}

const getEmployeeDisplayName = (employee: EmployeeWithStatus): string => {
  // Use label if available (includes status info), otherwise build name
  return employee.label || buildFullName(employee)
}

const isEmployeeInactive = (employee: EmployeeWithStatus): boolean => {
  return (
    employee.status !== undefined && employee.status !== EmployeeStatus.ACTIVE
  )
}

/**
 * EmployeeAvatarGroupForJobListing - Displays employee avatars with smart overflow handling
 *
 * Features:
 * - Shows up to maxVisible avatars (default 4)
 * - Prioritizes showing lead employees, then fills remaining slots with members
 * - Only shows "+N" overflow when N >= 2 (shows individual avatar instead of +1)
 * - Separates leads and members with a vertical divider
 * - Shows warning styling for inactive employees
 */
const EmployeeAvatarGroupForJobListing = ({
  leadEmployees,
  memberEmployees = [],
  maxVisible = 4,
}: EmployeeAvatarGroupProps) => {
  const primaryLead = leadEmployees[0]

  if (!primaryLead) {
    return (
      <div className="text-sm font-medium text-doubleNickel-gray-900">-</div>
    )
  }

  // Calculate visible slots for leads and members
  // Prioritize showing leads, then fill remaining slots with members
  // Only show "+N" overflow when N >= 2 (if N=1, show the person's initials instead)
  let maxLeadSlots = Math.min(leadEmployees.length, maxVisible)
  let remainingSlots = maxVisible - maxLeadSlots
  let maxMemberSlots = Math.min(memberEmployees.length, remainingSlots)

  // If lead overflow would be exactly 1, show that person instead of "+1"
  if (leadEmployees.length - maxLeadSlots === 1) {
    maxLeadSlots = leadEmployees.length
    remainingSlots = Math.max(0, maxVisible - maxLeadSlots)
    maxMemberSlots = Math.min(memberEmployees.length, remainingSlots)
  }

  // If member overflow would be exactly 1, show that person instead of "+1"
  if (memberEmployees.length - maxMemberSlots === 1) {
    maxMemberSlots = memberEmployees.length
  }

  const visibleLeads = leadEmployees.slice(0, maxLeadSlots)
  const leadOverflow = leadEmployees.slice(maxLeadSlots)
  const visibleMembers = memberEmployees.slice(0, maxMemberSlots)
  const memberOverflow = memberEmployees.slice(maxMemberSlots)

  const renderEmployeeAvatar = (employee: EmployeeWithStatus) => {
    const isInactive = isEmployeeInactive(employee)
    return (
      <Tooltip
        key={getEmployeeKey(employee)}
        label={getEmployeeDisplayName(employee)}
        withArrow
      >
        <Avatar
          classNames={{
            placeholder: isInactive
              ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
              : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
          }}
          size="sm"
          radius="sm"
        >
          {getEmployeeInitials(employee)}
        </Avatar>
      </Tooltip>
    )
  }

  const renderOverflowAvatar = (overflowEmployees: EmployeeWithStatus[]) => {
    const allInactive = overflowEmployees.every((emp) =>
      isEmployeeInactive(emp)
    )
    return (
      <Tooltip
        label={overflowEmployees.map((emp) => getEmployeeDisplayName(emp)).join(', ')}
        withArrow
      >
        <Avatar
          classNames={{
            placeholder: allInactive
              ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
              : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
          }}
          size="sm"
          radius="sm"
        >
          +{overflowEmployees.length}
        </Avatar>
      </Tooltip>
    )
  }

  return (
    <div className="flex items-center">
      <Avatar.Group spacing="0">
        {visibleLeads.map((lead) => renderEmployeeAvatar(lead))}
        {leadOverflow.length >= 2 && renderOverflowAvatar(leadOverflow)}
      </Avatar.Group>
      {memberEmployees.length > 0 && (
        <>
          <span className="mx-1.5 text-xs text-doubleNickel-gray-300">|</span>
          <Avatar.Group spacing="0">
            {visibleMembers.map((member) => renderEmployeeAvatar(member))}
            {memberOverflow.length >= 2 && renderOverflowAvatar(memberOverflow)}
          </Avatar.Group>
        </>
      )}
    </div>
  )
}

export default EmployeeAvatarGroupForJobListing
